import React from "react";
import { createBrowserRouter, Outlet, Navigate, useParams } from "react-router-dom";

import Main from "./layout";
import HomePage from "page/HomePage";
import PrivacyPolicyPage from "page/PrivacyPolicyPage";
import TermsPage from "page/TermsPage";
import NotFoundPage from "page/NotFoundPage";

export enum AppRoutes {
  Home = "",                 // was "/"
  PrivacyPolicy = "privacy-policy",
  Terms = "terms",
}

export const pathWithLang = (lang: "en" | "ua", route: AppRoutes) =>
  `/${lang}/${route === AppRoutes.Home ? "" : route}` || `/${lang}/`;

/**
 * Detects locale from Accept-Language header or localStorage
 * Returns "en" or "ua"
 */
function detectLocaleFromHeader(): "en" | "ua" {
  // First check localStorage preference
  const stored = (localStorage.getItem("lng") || "").slice(0, 2);
  if (stored === "uk" || stored === "ua") return "ua";
  if (stored === "en") return "en";

  // Parse Accept-Language header
  const acceptLanguage = navigator.language || "en";
  const lang = acceptLanguage.toLowerCase();
  
  // If Ukrainian, Russian, or any "uk"/"ua"/"ru" variant detected -> ua
  if (lang.startsWith("uk") || lang.startsWith("ua") || lang.startsWith("ru")) {
    return "ua";
  }
  
  // Default to English
  return "en";
}

function AutoHome() {
  // "/" -> "/en/" or "/ua/" based on Accept-Language / localStorage
  const routeLang = detectLocaleFromHeader();
  return <Navigate to={`/${routeLang}/`} replace />;
}

function LocaleRedirect({ route }: { route: "privacy-policy" | "terms" }) {
  // /privacy-policy or /terms -> /{locale}/privacy-policy or /{locale}/terms
  const routeLang = detectLocaleFromHeader();
  return <Navigate to={`/${routeLang}/${route}`} replace />;
}

function LangLayout() {
  const { lang } = useParams();            // "en" | "ua"
  if (lang !== "en" && lang !== "ua") return <Navigate to="/en/" replace />;
  return <Main><Outlet/></Main>;
}

const router = createBrowserRouter([
  { path: "/", element: <AutoHome /> },

  // Mobile deep links - redirect to locale-prefixed routes
  { path: "/privacy-policy", element: <LocaleRedirect route="privacy-policy" /> },
  { path: "/terms", element: <LocaleRedirect route="terms" /> },

  {
    path: "/:lang",
    element: <LangLayout />,
    children: [
      { index: true, element: <HomePage/> },
      { path: "privacy-policy", element: <PrivacyPolicyPage/> },
      { path: "terms", element: <TermsPage/> },
      { path: "*", element: <NotFoundPage/> }
    ]
  },

  { path: "*", element: <Navigate to="/en/" replace /> }
]);

export default router;
